import {Spin} from 'antd'
import {docs_fetchAdjDocumentRows_Api, docs_fetchAdjDocumentsValidOnCodiDocumentRow_Api,} from 'apis'
import EmptyHolder from 'components/EmptyHolder'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useEffect, useMemo, useState} from 'react'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault} from 'views/Shared'
import TooltipApp from "components/TooltipApp";
import {formatDate} from "../../../helpers/datetimeHelpers";

export const CodiDocumentSelector = ({
                                       current_row_id,
                                       timeout = 300,
                                       ...props
                                     }) => {
  const t = useTranslate()

  const [options, setOptions] = useState([])

  const [fetching, setFetching] = useState(false)

  const {isLoading = false, handleAsyncAction: fetchData = NullAsDefault} =
    useAsyncAction({
      apiInfo: docs_fetchAdjDocumentsValidOnCodiDocumentRow_Api,
      onError: notifyOnError(t),
      onSuccess: (__, data) => {
        const newOptions = _.get(data, 'response.data.data', [])
        setOptions(transformOptions(newOptions))
        setFetching(false)
      },
    })

  const transformOptions = (options = []) => {
    return Array.from(options).map(
      ({title, eff_date, description, ...item}) => ({
        ...item,
        title,
        label: (
          <TooltipApp title={title}>
            <div className="flex flex-col custom-select-mention">
              <div className="flex items-center gap-1 font-bold text-xs italic">
                <span className="text-color-000">
                  {`${formatDate(eff_date, 'DD/MM/YYYY')} - `}
                </span>
                <span className="text-primary truncate">{title}</span>
              </div>
              <div className="flex items-center leading-tight">
                {_.get(item, 'document_no') && (
                  <span className="font-medium text-2xs text-color-100 italic">
                    <span className="font-light text-color-400">
                        {`${t('document no')} : `}
                    </span>
                    <span>{_.get(item, 'document_no')}</span>
                  </span>
                )}
              </div>
            </div>
          </TooltipApp>
        ),
        value: item?.id,
      })
    )
  }

  useEffect(() => {
    fetchData({}, {id: current_row_id})
  }, [current_row_id])

  const debounceFetcher = useMemo(() => {
    const loadOptions = (keyword) => {
      setOptions([])
      setFetching(true)
      fetchData({keyword}, {id: current_row_id})
    }
    return _.debounce(loadOptions, timeout)
  }, [timeout, current_row_id])

  return (
    <SelectWidget
      allowClear={true}
      showSearch={true}
      labelInValue={true}
      filterOption={false}
      loading={!!isLoading}
      optionLabelProp="title"
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small"/> : <EmptyHolder/>}
      {...props}
      options={isLoading ? [] : options}
    />
  )
}

export const CodiDocumentRowSelector = ({
                                          codi_document,
                                          timeout = 300,
                                          ...props
                                        }) => {
  const t = useTranslate()

  const [options, setOptions] = useState([])

  const [fetching, setFetching] = useState(false)

  const codi_document_id = codi_document?.id

  const transformOptions = (options = []) =>
    Array.from(options).map(({short_index_name, content, ...item}) => ({
      content,
      short_index_name,
      title: `${short_index_name} ${content}`,
      label: (
        <TooltipApp title={content}>
          <div className="flex flex-col custom-select-mention">
            <div className="text-color-000 font-semibold text-sm max-lines-2">
              {short_index_name}
            </div>
            <div className="text-xs italic font-light text-color-400 leading-tight">
              {content}
            </div>
          </div>
        </TooltipApp>
      ),
      ...item,
      value: item?.id,
    }))

  const {isLoading = false, handleAsyncAction: fetchData = NullAsDefault} =
    useAsyncAction({
      onError: notifyOnError(t),
      apiInfo: docs_fetchAdjDocumentRows_Api,
      query: bindQueryParam({id: codi_document_id}),
      onSuccess: (__, data) => {
        const newOptions = _.get(data, 'response.data.data', [])
        setOptions(transformOptions(newOptions))
        setFetching(false)
      },
    })

  useEffect(() => {
    fetchData({}, {id: codi_document_id})
  }, [codi_document_id])

  const debounceFetcher = useMemo(() => {
    const loadOptions = (keyword) => {
      setOptions([])
      setFetching(true)
      fetchData({keyword}, {id: codi_document_id})
    }
    return _.debounce(loadOptions, timeout)
  }, [timeout, codi_document_id])

  if (_.isEmpty(codi_document_id)) {
    return null
  }

  return (
    <SelectWidget
      allowClear={true}
      showSearch={true}
      labelInValue={true}
      filterOption={false}
      loading={!!isLoading}
      optionLabelProp="title"
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small"/> : <EmptyHolder/>}
      {...props}
      options={options}
    />
  )
}
